import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * Loading placeholders.
 *
 * A skeleton has to match the layout it will become. One that shifts the page
 * when it resolves is worse than a spinner, because it has promised a shape and
 * then broken it - so these take explicit dimensions rather than guessing.
 *
 * `data-testid="skeleton"` is what the render harness waits to disappear
 * before it audits a screen.
 */
export function Skeleton({
  className,
  rounded = 'control',
  ...rest
}: {
  className?: string;
  rounded?: 'control' | 'card' | 'chip';
} & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return (
    <span
      {...rest}
      data-component="Skeleton"
      data-testid="skeleton"
      aria-hidden
      className={cx(
        'block animate-pulse bg-sunken',
        rounded === 'card' ? 'rounded-card' : rounded === 'chip' ? 'rounded-chip' : 'rounded-control',
        className,
      )}
    />
  );
}

/**
 * A loading region that says what it is loading.
 *
 * Never a bare spinner: the label is the difference between "this is broken"
 * and "this is coming".
 */
export function LoadingRegion({
  label,
  children,
  ...rest
}: { label: string; children: ReactNode } & HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div {...rest} data-component="LoadingRegion" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

export function SkeletonRows({
  rows = 5,
  className,
  ...rest
}: { rows?: number; className?: string } & HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div {...rest} className={cx('flex flex-col gap-2', className)}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable w-full" />
      ))}
    </div>
  );
}
